import React, { useState, useEffect, useMemo } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import * as api from 'utils/api';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from 'components/Common/Layout';
import { ReactComponent as BackArrow } from 'assets/images/back.svg';
import useTranslation from 'hooks/useTranslation';
import ReactLoading from 'components/Common/ReactLoading';
import { getFileUrl } from 'utils/file';
import orderedBy from 'lodash/orderBy';
import emitter, { Events } from 'utils/event';
import { ReactComponent as BoostIcon } from 'assets/images/boost.svg';
import { ReactComponent as RegenerateIcon } from 'assets/images/regenerate.svg';
import { ReactComponent as CoinIcon } from 'assets/images/Auto-Coin.svg';
import { useToastContext } from 'auto-design-common';
import { useAppContext } from 'components/AppContext';
import { useModalContext } from 'components/ModalContext';
import { ModalKey } from 'constants/modal';
import { ReactComponent as ShareIcon } from 'assets/images/share.svg';
import { ReactComponent as PaperPlaneIcon } from 'assets/images/paperplane.svg';
import { ReactComponent as GenAIFeedback1Icon } from 'assets/images/gen-ai-feedback-1.svg';
import { ReactComponent as GenAIFeedback2Icon } from 'assets/images/gen-ai-feedback-2.svg';
import AIProcessingIllustration from './AIProcessingIllustration';
import SadIllustration from './SadIllustration';
import styles from './GenAIImage.module.scss';

const getFileId = (variant) => variant.highResFileId || variant.lowResFileId;

export default function GenAIImage() {
  const { id } = useParams();
  const history = useHistory();
  const { tGenAIImage, tGenAIRoomTypes, tGenAIStyles, tCreateGenAIImage } = useTranslation();
  const [selectedVariantIndex, setSelectedVariantIndex] = useState(null);
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['GetGenAIImage', id],
    queryFn: () => api.getGenAIImage(id),
  });
  const [submitting, setSubmitting] = useState(false);
  const { toastError } = useToastContext();
  const { user, configs, getInfo } = useAppContext();
  const { showModal } = useModalContext();

  const variants = useMemo(() => {
    if (!data) {
      return [];
    }

    return orderedBy(data.variants, (variant) => {
      const createdAt = new Date(variant.createdAt);
      return createdAt.getTime();
    }, ['desc']);
  }, [data]);

  const selectedVariant = variants[selectedVariantIndex];

  const boostResolution = async () => {
    setSubmitting(true);

    try {
      await api.enhanceGenAIImage(id, selectedVariant.id);
      queryClient.invalidateQueries({ queryKey: ['GetGenAIImage', id] });
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
      getInfo();
    }
  };

  const regenerate = async () => {
    if (user.remainingFreeGenAiImages <= 0 && user.balance < configs.gen_ai_image_price) {
      showModal(ModalKey.TOP_UP, {
        title: tCreateGenAIImage('youDonHaveEnoughCoins'),
        subtitle: (
          <div className={styles.feelFreeToAddMore}>
            {tCreateGenAIImage('feelFreeToAddMore')}
          </div>
        ),
      });
      return;
    }

    setSubmitting(true);
    try {
      await api.regenerateGenAIImage(id);
      queryClient.invalidateQueries({ queryKey: ['GetGenAIImage', id] });
    } catch (error) {
      toastError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const share = async () => {
    try {
      let shareableId = data.shareableId;

      if (!data.shareableId) {
        const res = await api.shareGenAIImage(id);
        shareableId = res.shareableId;
      }

      const url = `${window.location.origin}/api/gen-ai-images/views/${shareableId}`;

      showModal(ModalKey.SHARE_POPUP, {
        shareUrl: url,
      });
    } catch (error) {
      toastError(error);
    }
  };

  const inviteFriends = () => {
    showModal(ModalKey.INVITE_FRIENDS);
  };

  const goToDIYService = () => {
    history.push('/add-design');
  };

  const goToHireUs = () => {
    window.open('https://betates.com', '_blank');
  };

  const openFeedbackModal = () => {
    window.open(getFileUrl(getFileId(selectedVariant)), '_blank');
    showModal(ModalKey.GEN_AI_IMAGE_FEEDBACK, {
      variants,
      selectedVariant,
      onFeedback: (isHappy) => {
        console.log('User feedback:', isHappy ? 'satisfied' : 'not satisfied');
        // Here you could send the feedback to your analytics or API
      },
    });
  };

  useEffect(() => {
    if (variants.length > 0) {
      setSelectedVariantIndex(selectedVariantIndex => {
        if (selectedVariantIndex === null) {
          return 0;
        }

        return selectedVariantIndex;
      });
    }
  }, [variants]);

  useEffect(() => {
    const listener = emitter.addListener(Events.NEW_NOTIFICATION, (data) => {
      if ((data.type === 'gen_ai_image_completed' || data.type === 'gen_ai_image_failed') && data.data.id === parseInt(id)) {
        queryClient.invalidateQueries({ queryKey: ['GetGenAIImage', id] });
      }
    });

    return () => {
      listener.remove();
    };
  }, [id, queryClient]);

  const renderContent = () => (
    <>
      <div className={styles.contentWrapper}>
        <div className={styles.content}>
          <div className={styles.preview}>
            <div className={styles.imageContainer}>
              <img src={getFileUrl(getFileId(selectedVariant))} alt="Preview" />
              <div className={styles.info}>
                <span>{tGenAIRoomTypes(data.roomType)}</span>
                <div className={styles.separator} />
                <span>{tGenAIStyles(data.style)}</span>
              </div>
            </div>
          </div>
          <div className={styles.variants}>
            {variants.map((variant, index) => (
              <div
                className={`${styles.variant} ${selectedVariant.id === variant.id ? styles.selected : ''}`}
                key={variant.id}
                onClick={() => {
                  setSelectedVariantIndex(index);
                }}
              >
                <img src={getFileUrl(getFileId(variant))} alt="Variant" />
                <div className={styles.variantName}>
                  {tGenAIImage('variants', { index: index + 1 })}
                </div>
              </div>
            ))}
          </div>
        </div>
        {submitting && (
          <div className={styles.overlay}>
            <div className={styles.overlayContent}>
              <div className={styles.spinner} />
              <div className={styles.overlayMessage}>
                {tGenAIImage('processing')}
              </div>
            </div>
          </div>
        )}
      </div>
      <div className={styles.footer}>
        <div className={styles.left}>
          <button
            type="button"
            className={styles.ctaButton}
            onClick={boostResolution}
            disabled={submitting || !!selectedVariant.highResFileId}
          >
            <BoostIcon />
            {tGenAIImage('boostResolution')}
          </button>
          <button type="button" className={styles.ctaButton} onClick={regenerate} disabled={submitting}>
            <RegenerateIcon />
            {tGenAIImage('regenerate')}
            {user.remainingFreeGenAiImages <= 0 ? (
              <div className={styles.price}>
                (
                {configs.gen_ai_image_price}
                {' x'}
                <CoinIcon />
                {tCreateGenAIImage('forGenerations', {
                  count: configs.free_gen_ai_images_per_coin * configs.gen_ai_image_price,
                })}
                )
              </div>
            ) : (
              <div className={styles.freeGenerationsLeft}>
                (
                {tCreateGenAIImage('freeGenerationsLeft', { count: user.remainingFreeGenAiImages })}
                )
              </div>
            )}
          </button>
          <div className={styles.separator} />
          <button type="button" onClick={share} className={styles.ctaButton2}>
            <ShareIcon />
            {tGenAIImage('share')}
          </button>
          <button type="button" onClick={inviteFriends} className={styles.ctaButton2}>
            <PaperPlaneIcon />
            {tGenAIImage('referAFriend')}
          </button>
          <div className={styles.separator} />
          <button type="button" onClick={goToDIYService} className={styles.ctaButton2}>
            <GenAIFeedback1Icon width={24} height={24} />
            {tGenAIImage('diyService')}
          </button>
          <button type="button" onClick={goToHireUs} className={styles.ctaButton2}>
            <GenAIFeedback2Icon width={24} height={24} />
            {tGenAIImage('hireUs')}
          </button>
        </div>
        <button
          className={styles.downloadButton}
          type="button"
          onClick={openFeedbackModal}
        >
          {tGenAIImage('download')}
        </button>
      </div>
    </>
  );

  const renderProcessingState = () => (
    <div className={styles.processingContainer}>
      <AIProcessingIllustration />
      <div className={styles.processingMessage}>
        {tGenAIImage('processing')}
      </div>
    </div>
  );

  return (
    <Layout>
      <div className={styles.genAIImageContainer}>
        <div className={styles.header}>
          <button
            type="button"
            className={styles.backButton}
            onClick={() => {
              history.replace('/gen-ai-images/history');
            }}
          >
            <BackArrow />
          </button>
          <div className={styles.title}>
            {tGenAIImage('title')}
          </div>
        </div>
        {isLoading && <ReactLoading />}
        {data && data.status === 'completed' && selectedVariant && renderContent()}
        {data && data.status !== 'completed' && data.status !== 'failed' && renderProcessingState()}
        {data && data.status === 'failed' && (
          <div className={styles.failedContainer}>
            <SadIllustration />
            <div className={styles.failedMessage}>
              {tGenAIImage('failed')}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
